import { Injectable } from '@angular/core';
import { AbstractControl, Validators } from '@angular/forms';

import { Observable, of } from 'rxjs';
import { map, tap, shareReplay } from 'rxjs/operators';

import { Country, OceanPort, ShipmentTypes } from '@library/common/models';

import { EnumsService } from './enums.service';
import { CacheService } from './cache.service';

@Injectable({
   providedIn: 'root'
})
export class CountriesService {

   constructor(
      private enums: EnumsService,
      private cache: CacheService,
   ) {}

   get countries(): Observable<Country[]> {
      if ( this.cache.check( 'countries' )) {
         return of( this.cache.retrieve( 'countries' ));
      }

      return this.enums.countries().pipe(
         map( countries => countries.sort(( a, b ) => a.country < b.country ? -1 : a.country > b.country ? 1 : 0 )),
         tap( countries => this.cache.store( 'countries', countries, 1400 )),
         shareReplay(),
      );
   }

   countriesByDomesticFactor( type: ShipmentTypes ): Observable<Country[]> {
      return this.countries.pipe(
         map( countries => {
            switch ( type ) {
               case 'domestic':
                  return countries.filter( c => [ 'US', 'CA', 'MX' ].includes( c.code ));
               default:
                  return countries;
            }
         }),
      );
   }

   country( code: string ): Observable<Country> {
      return this.countries.pipe(
         map( countries => countries.filter( c => c.code === code )[0] ),
      );
   }

   countryName( code: string ): Observable<string> {
      return this.country( code ).pipe( map( c => c?.country ?? '' ));
   }

   ports( code: string ): Observable<OceanPort[]> {
      return this.enums.oceanPorts().pipe(
         map( ports => ports.filter( port => port.code === code )),
      );
   }

   allPorts(): Observable<string[]> {
      return this.countries.pipe(
         map( countries => countries.reduce(( a, c ) => [ ...a, ...c.ports ], [] ) ),
      );
   }

   setPostalCodeValidators( code: string, control: AbstractControl ): void {
      if ( !code || !control ) { return; }

      this.country( code ).subscribe( country => {
         const deets = country?.postalDetails;
         if ( !deets ) { return; }

         const validators = [
            deets.regex ? Validators.pattern( new RegExp( deets.regex )) : null,
            deets.required ? Validators.required : null,
            deets.maxLength ? Validators.maxLength( deets.maxLength ) : null,
         ].filter( _ => _ );

         control.setValidators( validators );
         control.updateValueAndValidity();
      });
   }
}
